import React, { useState, useEffect } from 'react';
import { supabase } from '@/supabaseClient';
import { api } from '../api';
import * as Sentry from '@sentry/browser';

export function EditRequestForm({ request, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: request?.title || '',
    description: request?.description || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingName, setIsGeneratingName] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (request) {
      setFormData({
        title: request.title,
        description: request.description
      });
    }
  }, [request]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    setErrors({});

    try {
      const updatedRequest = await api.updateRequest({
        requestId: request.id,
        title: formData.title,
        description: formData.description
      });
      
      onSave(updatedRequest);
    } catch (error) {
      console.error('Error updating request:', error);
      Sentry.captureException(error, {
        extra: {
          requestId: request.id,
          formData,
          errorMessage: error.message
        }
      });
      
      if (error.message.includes('validation')) {
        setErrors({ form: 'Please check your input and try again.' });
      } else if (error.message.includes('403')) {
        setErrors({ form: 'You can only edit your own requests.' });
      } else if (error.message.includes('404')) {
        setErrors({ form: 'Request not found.' });
      } else {
        setErrors({ form: 'Failed to update request. Please try again.' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleGenerateAppName = async () => {
    if (!formData.description.trim() || isGeneratingName) return;

    setIsGeneratingName(true);
    setErrors({});

    try {
      console.log('Generating app name for description:', formData.description.substring(0, 100) + '...');
      
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch('/api/generate-app-name', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ description: formData.description }),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.warn('Authentication error in app name generation, user needs to re-authenticate');
          setErrors({ generateName: 'Authentication expired. Please refresh the page and try again.' });
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate app name');
      }

      const data = await response.json();
      console.log('Generated app name received:', data.generatedName);
      
      setFormData(prev => ({
        ...prev,
        title: data.generatedName
      }));
    } catch (error) {
      console.error('Error generating app name:', error);
      Sentry.captureException(error, {
        extra: { 
          description: formData.description,
          errorMessage: error.message 
        }
      });
      
      setErrors({ generateName: 'Failed to generate app name. Please try again.' });
    } finally {
      setIsGeneratingName(false);
    }
  };

  const titleCharCount = formData.title.length;
  const descCharCount = formData.description.length;
  const isFormValid = formData.title.trim() && formData.description.trim() && titleCharCount <= 200 && descCharCount <= 2000;
  const hasChanges = formData.title !== request?.title || formData.description !== request?.description;

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Form Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-4 sm:px-8 sm:py-6">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Edit Your Request</h3>
        <p className="text-blue-100 text-sm sm:text-base">Update your app idea details</p>
      </div>
      
      <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-6">
        {/* Title Field */}
        <div>
          <label htmlFor="edit-title" className="block text-sm font-semibold text-gray-700 mb-2">
            App Title *
          </label>
          <div className="space-y-3">
            <input
              type="text"
              id="edit-title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., AI-Powered Fitness Tracker, Smart Home Manager"
              className="w-full px-3 py-3 sm:px-4 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent box-border text-gray-900 text-base sm:text-lg shadow-sm hover:shadow-md transition-shadow"
              maxLength={200}
              required
            />
            
            {/* AI Generate Button */}
            <button
              type="button"
              onClick={handleGenerateAppName}
              disabled={!formData.description.trim() || isGeneratingName}
              className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium cursor-pointer transition-all duration-200 active:scale-95 disabled:transform-none flex items-center justify-center text-sm sm:text-base"
            >
              {isGeneratingName ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Generating Name...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Regenerate App Name with AI
                </>
              )}
            </button>
            
            {/* Error for name generation */}
            {errors.generateName && (
              <div className="text-red-600 text-sm">
                {errors.generateName}
              </div>
            )}
          </div>
          
          <div className="flex justify-between items-center mt-2">
            <div className="text-xs text-gray-500">
              Keep it clear and descriptive
            </div>
            <div className={`text-xs ${titleCharCount > 180 ? 'text-red-500' : 'text-gray-500'}`}>
              {titleCharCount}/200 characters
            </div>
          </div>
        </div>

        {/* Description Field */}
        <div>
          <label htmlFor="edit-description" className="block text-sm font-semibold text-gray-700 mb-2">
            App Description *
          </label>
          <textarea
            id="edit-description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe your app idea in detail:
• What problem does it solve?
• Who would use it?
• What are the key features?
• Why would people pay for it?

Be specific and inspiring!"
            rows={6}
            className="w-full px-3 py-3 sm:px-4 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent box-border text-gray-900 shadow-sm hover:shadow-md transition-shadow resize-none text-sm sm:text-base"
            maxLength={2000}
            required
          />
          <div className="flex justify-between items-center mt-2">
            <div className="text-xs text-gray-500">
              The more detailed, the better
            </div>
            <div className={`text-xs ${descCharCount > 1800 ? 'text-red-500' : 'text-gray-500'}`}>
              {descCharCount}/2000 characters
            </div>
          </div>
        </div>

        {/* Error Display */}
        {errors.form && (
          <div className="bg-red-50 border border-red-200 rounded-lg sm:rounded-xl p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-800">Error updating request</p>
                <p className="text-sm text-red-600">{errors.form}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-4 pt-4">
          <button
            type="submit"
            disabled={!isFormValid || !hasChanges || isSaving}
            className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 sm:py-4 sm:px-6 rounded-lg sm:rounded-xl hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-semibold cursor-pointer transition-all duration-200 active:scale-95 disabled:transform-none flex items-center justify-center text-sm sm:text-base"
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                Saving Changes...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save Changes
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-3 sm:px-6 sm:py-4 text-gray-600 hover:text-gray-800 hover:bg-gray-100 font-semibold cursor-pointer rounded-lg sm:rounded-xl transition-colors text-sm sm:text-base"
          >
            Cancel
          </button>
        </div>

        {/* Tips */}
        {!hasChanges && (
          <div className="bg-blue-50 rounded-lg sm:rounded-xl p-4 mt-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-blue-700">Make changes to enable the save button</p>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}